import type { FC } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';

import AcceptInvitePage from '@/features/auth/pages/AcceptInvitePage';
import AnalyticsPage from '@/features/analytics/pages/AnalyticsPage';
import CampusesPage from '@/features/campuses/pages/CampusesPage';
import LoginPage from '@/features/auth/pages/LoginPage';
import ContactMessagesPage from '@/features/contact-messages/pages/ContactMessagesPage';
import DashboardPage from '@/features/dashboard/pages/DashboardPage';
import ChildSafetyPage from '@/features/legal/pages/ChildSafetyPage';
import SupportPage from '@/features/legal/pages/SupportPage';
import DeleteAccountPage from '@/features/legal/pages/DeleteAccountPage';
import UnsubscribePage from '@/features/legal/pages/UnsubscribePage';
import HomePage from '@/features/home/pages/HomePage';
import PolicyPage from '@/features/legal/pages/PolicyPage';
import RentalTermsPage from '@/features/legal/pages/RentalTermsPage';
import TermsPage from '@/features/legal/pages/TermsPage';
import ModerationPage from '@/features/moderation/pages/ModerationPage';
import NotificationsPage from '@/features/notifications/pages/NotificationsPage';
import PaymentsPage from '@/features/payments/pages/PaymentsPage';
import RentalsPage from '@/features/rentals/pages/RentalsPage';
import SettingsPage from '@/features/settings/pages/SettingsPage';
import UsersListPage from '@/features/users/pages/UsersListPage';
import AuthLayout from '@/layouts/AuthLayout';
import DashboardLayout from '@/layouts/DashboardLayout';
import PublicLayout from '@/layouts/PublicLayout';
import RequireAuth from '@/routes/guards/RequireAuth';
import RequireRole from '@/routes/guards/RequireRole';
import { ROUTES } from '@/routes/route.config';
import { ADMIN_ROLES } from '@/types/user.types';

const AppRouter: FC = () => (
  <Routes>
    {/* Public marketing site — the admin panel lives behind /login. */}
    <Route element={<PublicLayout />}>
      <Route path={ROUTES.home} element={<HomePage />} />
      <Route path={ROUTES.terms} element={<TermsPage />} />
      <Route path={ROUTES.rentalTerms} element={<RentalTermsPage />} />
      <Route path={ROUTES.privacy} element={<PolicyPage />} />
      <Route path={ROUTES.deleteAccount} element={<DeleteAccountPage />} />
      <Route path={ROUTES.childSafety} element={<ChildSafetyPage />} />
      <Route path={ROUTES.support} element={<SupportPage />} />
      {/* Reached from a link inside an onboarding nudge email — no account, no session. */}
      <Route path={ROUTES.unsubscribe} element={<UnsubscribePage />} />
    </Route>

    <Route element={<AuthLayout />}>
      <Route path={ROUTES.login} element={<LoginPage />} />
      {/* An invited admin has no password yet, so this sits outside RequireAuth. */}
      <Route path={ROUTES.acceptInvite} element={<AcceptInvitePage />} />
    </Route>

    <Route element={<RequireAuth />}>
      <Route element={<DashboardLayout />}>
        <Route path={ROUTES.dashboard} element={<DashboardPage />} />
        <Route path={ROUTES.analytics} element={<AnalyticsPage />} />
        <Route path={ROUTES.users} element={<UsersListPage />} />
        <Route path={ROUTES.rentals} element={<RentalsPage />} />
        {/* Moderators may work the moderation queue... */}
        <Route element={<RequireRole roles={ADMIN_ROLES} />}>
          <Route path={ROUTES.moderation} element={<ModerationPage />} />
        </Route>
        {/* ...but the server gates payments and settings to super_admin/admin, so guarding
            these with ADMIN_ROLES (which is every role) only sent moderators to collect 403s. */}
        <Route element={<RequireRole roles={['super_admin', 'admin']} />}>
          <Route path={ROUTES.payments} element={<PaymentsPage />} />
          <Route path={ROUTES.settings} element={<SettingsPage />} />
        </Route>
        <Route path={ROUTES.campuses} element={<CampusesPage />} />
        <Route path={ROUTES.notifications} element={<NotificationsPage />} />
        <Route path={ROUTES.contactMessages} element={<ContactMessagesPage />} />
      </Route>
    </Route>

    <Route path="*" element={<Navigate to={ROUTES.home} replace />} />
  </Routes>
);

export default AppRouter;
